import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Theme, useTheme } from "@mui/material";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
const UK_CENTER: [number, number] = [-1.895662, 52.489706];
const INITIAL_ZOOM = 6;
const PROJECT_ZOOM = 16;
const ANIMATION_DURATION = 3000;
const PROJECT_DWELL_TIME = 120000;
const ROTATION_DURATION = 115000;
const ROTATION_INTERVAL_MS = 300;
const CAMERA_PITCH = 50;
const MARKER_SCALE_DURATION = 500;
const MARKER_SCALE_FACTOR = 1.3;
const RESIZE_THROTTLE_MS = 250;

interface Project {
  id: number;
  title?: string;
  latitude?: number;
  longitude?: number;
  address?: string;
}

interface UseLiveProgressMapOptions {
  projects: Project[];
  onProjectChange?: (project: Project | null) => void;
}

interface MapRefs {
  map: mapboxgl.Map | null;
  markers: Map<number, mapboxgl.Marker>;
  animationFrame: number | null;
  rotationInterval: NodeJS.Timeout | null;
  currentProjectIndex: number;
  isAnimating: boolean;
  resizeTimeout: NodeJS.Timeout | null;
}

const hasValidCoordinates = (project: Project): boolean => {
  return (
    project.latitude !== undefined &&
    project.longitude !== undefined &&
    !isNaN(project.latitude) &&
    !isNaN(project.longitude)
  );
};

const add3DBuildings = (map: mapboxgl.Map, theme: Theme) => {
  if (map.getLayer("3d-buildings")) {
    return;
  }

  const style = map.getStyle();
  if (!style || !style.layers || !style.sources) {
    return;
  }

  const layers = style.layers;
  let beforeLayerId: string | undefined;
  for (let i = layers.length - 1; i >= 0; i--) {
    const layer = layers[i];
    if (
      layer.type === "symbol" &&
      layer.layout &&
      "text-field" in layer.layout
    ) {
      beforeLayerId = layer.id;
      break;
    }
  }

  const sources = style.sources;
  let buildingSource: string | undefined;
  let sourceLayer = "building";

  for (const sourceId in sources) {
    const source = sources[sourceId];
    if (source.type === "vector") {
      const vectorSource = source as {
        type: string;
        url?: string;
        tiles?: string[];
      };
      const url = vectorSource.url || "";
      if (
        url.includes("mapbox-streets") ||
        url.includes("composite") ||
        sourceId.includes("composite") ||
        sourceId.includes("mapbox")
      ) {
        buildingSource = sourceId;
        break;
      }
    }
  }

  if (!buildingSource) {
    const firstVectorSource = Object.keys(sources).find(
      (id) => sources[id].type === "vector"
    );
    if (firstVectorSource) {
      buildingSource = firstVectorSource;
    } else {
      return;
    }
  }

  try {
    map.addLayer(
      {
        id: "3d-buildings",
        source: buildingSource,
        "source-layer": sourceLayer,
        filter: ["==", "extrude", "true"],
        type: "fill-extrusion",
        minzoom: 14,
        paint: {
          "fill-extrusion-color": [
            "interpolate",
            ["linear"],
            ["get", "height"],
            0,
            theme.palette.background.paper,
            100,
            theme.palette.background.paper,
            200,
            theme.palette.background.paper,
          ],
          "fill-extrusion-height": ["*", ["get", "height"], 1],
          "fill-extrusion-base": ["*", ["get", "min_height"], 1],
          "fill-extrusion-opacity": 0.9,
        },
      },
      beforeLayerId
    );
  } catch (error) {
    console.warn("Could not add 3D buildings layer:", error);
  }
};

const getMapStyle = (isDarkMode: boolean): string => {
  return isDarkMode
    ? "mapbox://styles/mapbox/dark-v11"
    : "mapbox://styles/mapbox/standard";
};

export const useLiveProgressMap = ({
  projects,
  onProjectChange,
}: UseLiveProgressMapOptions) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRefs = useRef<MapRefs>({
    map: null,
    markers: new Map(),
    animationFrame: null,
    rotationInterval: null,
    currentProjectIndex: 0,
    isAnimating: false,
    resizeTimeout: null,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);

  const theme = useTheme();

  const projectsWithLocation = useMemo(
    () => projects.filter(hasValidCoordinates),
    [projects]
  );

  const stopRotation = useCallback(() => {
    if (mapRefs.current.rotationInterval) {
      clearInterval(mapRefs.current.rotationInterval);
      mapRefs.current.rotationInterval = null;
    }
  }, []);

  const rotateCamera = useCallback(
    (project: Project, startBearing: number) => {
      const { map } = mapRefs.current;
      if (!map || !project.latitude || !project.longitude) return;

      stopRotation();

      let currentBearing = startBearing;
      const rotationStep = 360 / (ROTATION_DURATION / ROTATION_INTERVAL_MS);

      mapRefs.current.rotationInterval = setInterval(() => {
        const { map } = mapRefs.current;
        if (!map) {
          stopRotation();
          return;
        }

        currentBearing = (currentBearing + rotationStep) % 360;

        map.easeTo({
          bearing: currentBearing,
          duration: ROTATION_INTERVAL_MS,
          easing: (t) => t,
        });
      }, ROTATION_INTERVAL_MS);

      setTimeout(() => {
        stopRotation();
        mapRefs.current.isAnimating = false;
      }, ROTATION_DURATION);
    },
    [stopRotation]
  );

  const animateMarker = useCallback((projectId: number) => {
    const marker = mapRefs.current.markers.get(projectId);
    if (!marker) return;

    const element = marker.getElement();
    element.style.transform = `scale(${MARKER_SCALE_FACTOR})`;

    setTimeout(() => {
      element.style.transform = "scale(1)";
    }, MARKER_SCALE_DURATION);
  }, []);

  const flyToProject = useCallback(
    (project: Project, index: number) => {
      const { map } = mapRefs.current;
      if (!map || !project.latitude || !project.longitude) return;

      mapRefs.current.isAnimating = true;
      setCurrentProject(project);
      onProjectChange?.(project);

      const initialBearing = index * 45;

      map.flyTo({
        center: [project.longitude, project.latitude],
        zoom: PROJECT_ZOOM,
        duration: ANIMATION_DURATION,
        essential: true,
        pitch: CAMERA_PITCH,
        bearing: initialBearing,
      });

      animateMarker(project.id);

      setTimeout(() => {
        rotateCamera(project, initialBearing);
      }, ANIMATION_DURATION);
    },
    [onProjectChange, rotateCamera, animateMarker]
  );

  const stopAnimation = useCallback(() => {
    if (mapRefs.current.animationFrame) {
      cancelAnimationFrame(mapRefs.current.animationFrame);
      mapRefs.current.animationFrame = null;
    }
  }, []);

  const startAnimation = useCallback(() => {
    const { map, isAnimating } = mapRefs.current;
    if (projectsWithLocation.length === 0 || !map || !isMapLoaded) return;

    const animate = () => {
      if (mapRefs.current.isAnimating) {
        mapRefs.current.animationFrame = requestAnimationFrame(animate);
        return;
      }

      const nextIndex =
        mapRefs.current.currentProjectIndex % projectsWithLocation.length;
      const project = projectsWithLocation[nextIndex];

      flyToProject(project, nextIndex);
      mapRefs.current.currentProjectIndex = nextIndex + 1;

      setTimeout(() => {
        mapRefs.current.animationFrame = requestAnimationFrame(animate);
      }, ANIMATION_DURATION + PROJECT_DWELL_TIME);
    };

    animate();
  }, [projectsWithLocation, isMapLoaded, flyToProject]);

  const cleanupMap = useCallback(() => {
    stopAnimation();
    stopRotation();
    if (mapRefs.current.resizeTimeout) {
      clearTimeout(mapRefs.current.resizeTimeout);
      mapRefs.current.resizeTimeout = null;
    }
    mapRefs.current.markers.forEach((marker) => marker.remove());
    mapRefs.current.markers.clear();
    mapRefs.current.map?.remove();
    mapRefs.current.map = null;
  }, [stopAnimation, stopRotation]);

  useEffect(() => {
    return () => {
      cleanupMap();
    };
  }, [cleanupMap]);

  useEffect(() => {
    if (!mapContainer.current || mapRefs.current.map || !MAPBOX_ACCESS_TOKEN) {
      if (!MAPBOX_ACCESS_TOKEN) {
        setError("MapBox access token is not configured");
        setIsLoading(false);
      }
      return;
    }

    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    try {
      const mapStyle = getMapStyle(theme.palette.mode === "dark");

      const mapInstance = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapStyle,
        center: UK_CENTER,
        zoom: INITIAL_ZOOM,
        pitch: 45,
        bearing: 0,
        antialias: false,
        trackResize: true,
        renderWorldCopies: false,
        maxPitch: 60,
      });

      mapInstance.on("load", () => {
        add3DBuildings(mapInstance, theme);
        setIsLoading(false);
        setIsMapLoaded(true);
      });

      mapInstance.on("error", (e: unknown) => {
        console.error("MapBox error:", e);
        setError("Failed to load map");
        setIsLoading(false);
      });

      mapRefs.current.map = mapInstance;
    } catch (err) {
      console.error("MapBox initialization error:", err);
      setError("Failed to initialize map");
      setIsLoading(false);
    }
  }, [theme.palette.mode]);

  useEffect(() => {
    const container = mapContainer.current;
    const { map } = mapRefs.current;

    if (!container || !map) return;

    const handleResize = () => {
      if (mapRefs.current.resizeTimeout) {
        clearTimeout(mapRefs.current.resizeTimeout);
      }
      mapRefs.current.resizeTimeout = setTimeout(() => {
        if (map && !map._removed) {
          map.resize();
        }
      }, RESIZE_THROTTLE_MS);
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (mapRefs.current.resizeTimeout) {
        clearTimeout(mapRefs.current.resizeTimeout);
      }
    };
  }, [isMapLoaded]);

  useEffect(() => {
    const { map } = mapRefs.current;
    if (!map || !isMapLoaded) return;

    const mapStyle = getMapStyle(theme.palette.mode === "dark");

    setIsMapLoaded(false);
    map.setStyle(mapStyle);
    map.once("styledata", () => {
      add3DBuildings(map, theme);
      setIsMapLoaded(true);
    });
  }, [theme.palette.mode]);

  const createMarkers = useCallback(() => {
    const { map, markers } = mapRefs.current;
    if (!map) return;

    markers.forEach((marker) => marker.remove());
    markers.clear();

    projectsWithLocation.forEach((project) => {
      if (project.latitude && project.longitude) {
        const marker = new mapboxgl.Marker({
          color: theme.palette.primary.main,
        })
          .setLngLat([project.longitude, project.latitude])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(
              `<strong>${project.title}</strong><br/>${project.address || ""}`
            )
          )
          .addTo(map);

        markers.set(project.id, marker);
      }
    });
  }, [projectsWithLocation, theme.palette.primary.main]);

  useEffect(() => {
    const { map } = mapRefs.current;
    if (!map || !isMapLoaded || projectsWithLocation.length === 0) return;

    createMarkers();

    if (projectsWithLocation.length > 1) {
      startAnimation();
    } else if (projectsWithLocation.length === 1) {
      const project = projectsWithLocation[0];
      flyToProject(project, 0);
    }

    return () => {
      stopAnimation();
    };
  }, [
    projectsWithLocation,
    isMapLoaded,
    createMarkers,
    startAnimation,
    flyToProject,
    stopAnimation,
  ]);

  const goToProject = useCallback(
    (projectId: number) => {
      const index = projectsWithLocation.findIndex((p) => p.id === projectId);
      if (index === -1) return;

      stopAnimation();
      mapRefs.current.currentProjectIndex = index;
      flyToProject(projectsWithLocation[index], index);

      setTimeout(() => {
        startAnimation();
      }, ANIMATION_DURATION + PROJECT_DWELL_TIME);
    },
    [projectsWithLocation, flyToProject, startAnimation, stopAnimation]
  );

  return {
    mapContainer,
    isLoading,
    error,
    currentProject,
    goToProject,
  };
};
